/** Pending invitation token for accept-invitation deep links. Never log the raw token. */

const STORAGE_KEY = "agencyos.pendingInvitationToken";

const readToken = (): string | null => {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value !== null && value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

const writeToken = (token: string): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // sessionStorage may be unavailable; acceptance still works if caller keeps the token.
  }
};

export const getPendingInvitationToken = (): string | null => readToken();

export const setPendingInvitationToken = (token: string): void => {
  writeToken(token);
};

export const clearPendingInvitationToken = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Capture `/accept-invitation?token=` into sessionStorage and strip the token from the URL.
 */
export const captureInvitationTokenFromLocation = (
  location: Pick<Location, "pathname" | "search" | "hash"> = window.location,
  replaceState: (url: string) => void = (url) => {
    window.history.replaceState({}, "", url);
  },
): string | null => {
  const path = location.pathname.replace(/\/+$/u, "") || "/";
  if (path !== "/accept-invitation") {
    return getPendingInvitationToken();
  }
  const params = new URLSearchParams(location.search);
  const token = params.get("token")?.trim() ?? "";
  if (token.length === 0) {
    return getPendingInvitationToken();
  }
  setPendingInvitationToken(token);
  params.delete("token");
  const query = params.toString();
  replaceState(`/accept-invitation${query.length > 0 ? `?${query}` : ""}${location.hash}`);
  return token;
};

/**
 * Read `/verify-email?token=` once and strip it from the URL.
 */
export const captureEmailVerificationTokenFromLocation = (
  location: Pick<Location, "pathname" | "search" | "hash"> = window.location,
  replaceState: (url: string) => void = (url) => {
    window.history.replaceState({}, "", url);
  },
): string | null => {
  const path = location.pathname.replace(/\/+$/u, "") || "/";
  if (path !== "/verify-email") {
    return null;
  }
  const params = new URLSearchParams(location.search);
  const token = params.get("token")?.trim() ?? "";
  params.delete("token");
  const query = params.toString();
  replaceState(`/verify-email${query.length > 0 ? `?${query}` : ""}${location.hash}`);
  return token.length > 0 ? token : null;
};
